import { css } from '@emotion/react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { ButtonHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Figma Button 의 Type 축. HTML button 의 `type` 속성과 충돌하므로 prop 이름은 variant 를 유지한다.
 * `text` 는 Xsmall 에서만 쓰도록 디자인이 정의돼 있다.
 */
type Variant = 'filled' | 'tone' | 'outlined' | 'text';
type Size = 'large' | 'medium' | 'small' | 'xsmall';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
    fullWidth?: boolean;
    variant?: Variant;
    size?: Size;
    noPadding?: boolean;
    /**
     * 디자인 토큰 밖의 색. 소셜 로그인(카카오·애플·구글)과 서버가 내려주는 정책 배너 색처럼
     * 토큰으로 관리하지 않기로 한 경우에만 사용한다. 그 외에는 variant 를 쓴다.
     */
    unmanagedColor?: string;
    unmanagedBackground?: string;
}

function Button(props: Props) {
    const {
        fullWidth, variant = 'filled', children, size = 'large', className,
        noPadding, unmanagedColor, unmanagedBackground,
        ...rest
    } = props;
    const { i18n: { language } } = useTranslation();
    return (<Base
        lang={language}
        unmanagedColor={unmanagedColor}
        unmanagedBackground={unmanagedBackground}
        className={classNames(
            `variant-${variant}`,
            `button-${fullWidth ? 'full' : 'auto'}`,
            `size-${size}`,
            { 'no-padding': noPadding },
            className,
        )}
        {...rest} >
        {children}
    </Base>);
}

const STYLE_PROPS = ['unmanagedColor', 'unmanagedBackground'];

const Base = styled('button', {
    shouldForwardProp: (prop) => !STYLE_PROPS.includes(prop),
})<{
    unmanagedColor?: string;
    unmanagedBackground?: string;
}>`
    align-items: center;
    border-radius: var(--radius-4);
    cursor: pointer;
    display: flex;
    font-weight: bold;
    justify-content: center;
    line-height: 1.4;
    position: relative;
    transition: 150ms;
    white-space: nowrap;

    &:active:not(:disabled) {
        transform: scale(.95);
    }

    /* ===== Size ===== */
    &.size-large {
        font-size: var(--font-size-16);
        height: 56px;
        padding: 16px 24px;
    }

    &.size-medium {
        font-size: var(--font-size-14);
        height: 50px;
        padding: 16px 20px;
    }

    &.size-small {
        font-size: var(--font-size-12);
        height: 40px;
        padding: 8px 24px;
    }

    &.size-xsmall {
        font-size: var(--font-size-12);
        height: 28px;
        padding: 4px 16px;
    }

    /* ===== Type ===== */
    &.variant-filled {
        background: var(--color-primary);
        color: var(--color-button-text);
    }

    &.variant-tone {
        background: var(--color-gray2);
        color: var(--color-gray7);
    }

    &.variant-outlined {
        background: var(--color-white);
        border: 1px solid var(--color-gray2);
        color: var(--color-gray7);
    }

    &.variant-text {
        background: transparent;
        color: var(--color-gray7);
    }

    &.button-full {
        width: 100%;
    }

    &.no-padding {
        padding-left: 0;
        padding-right: 0;
    }

    &:disabled {
        border: none !important;
        background-color: var(--color-gray2);
        color: var(--color-button-text-disabled);
        cursor: not-allowed;
    }

    &.variant-text:disabled {
        background-color: transparent;
    }

    ${({ unmanagedColor }) => (unmanagedColor ? css`
        color: ${unmanagedColor}  !important;
    ` : undefined)}

    ${({ unmanagedBackground }) => (unmanagedBackground ? css`
        background-color: ${unmanagedBackground} !important;
    ` : undefined)}
`;

export type { Variant as ButtonVariant, Size as ButtonSize };
export default Button;
